import React, { FC } from 'react';
import { Text, View } from 'react-native';
import OutsidePressHandler from 'react-native-outside-press';
import TouchableElement from '@components/common/touchableElement';
import ColorWrapper from '@styles/colors';
import { getBoxShadowStyle } from '@styles/shadow';
import { scale } from '@components/frame';
import Svg from '@styles/icons/svg';
import i18n from 'i18n-js';
import { font } from '@styles/fonts';
import TimeDisplayInStaticFormat from '@components/common/TimeDisplayInStaticFormat';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import { DOT_SIZE } from '@v2/pages/common/components/NCFileBatchEvents/constants';
import useManageTimeTile from './hooks/useManageTimeTile';
import { TimeTileConfig, OptimisticUpdateCallbacks } from '@v2/pages/common/components/NCFileBatchEvents/types';
import { ColorFunction } from '@styles/colors/types';
import TimeTileEditActions from '@v2/pages/common/components/NCFileBatchEvents/components/TimeTiles/components/TimeTileEditActions';
import TimeTileInput from '@v2/pages/common/components/NCFileBatchEvents/components/TimeTiles/components/TimeTileInput';
import { TILE_HEIGHT } from '@v2/pages/common/components/NCFileBatchEvents/components/TimeTiles/constants';
import { TimeTilesProps } from '@v2/pages/common/components/NCFileBatchEvents/components/TimeTiles';

export interface TimeTileProps extends Pick<TimeTilesProps, 'eventType' | 'eventTimelineData'>, OptimisticUpdateCallbacks {
  C: ColorFunction;
  timeTileConfig: TimeTileConfig;
  isReadyToShow: boolean;
  ncFileIdForUpdate: number | null | undefined;
  remeasureTrigger?: number;
}

const TimeTile: FC<TimeTileProps> = ({
  C,
  timeTileConfig,
  isReadyToShow,
  eventType,
  eventTimelineData,
  ncFileIdForUpdate,
  onOptimisticUpdate,
  onOptimisticUpdateError,
  remeasureTrigger,
}) => {
  const shadowStyles = getBoxShadowStyle({ type: 'Field', C });
  const { descriptionConfig, iconConfig, timeConfig, titleConfig } = timeTileConfig;

  const {
    dotRef,
    tileRef,
    isEditing,
    inputValue,
    parsedInputValue,
    originalValue,
    editable,
    handleTilePress,
    handleInputChange,
    handleCancel,
    handleConfirm,
  } = useManageTimeTile({
    timeTileConfig,
    eventType,
    eventTimelineData,
    ncFileIdForUpdate,
    onOptimisticUpdate,
    onOptimisticUpdateError,
    remeasureTrigger,
    isReadyToShow: isReadyToShow,
  });

  const tileStyles = [
    shadowStyles,
    {
      flex: 1,
      backgroundColor: C('Background/Light/Field'),
      paddingVertical: scale(8),
      paddingHorizontal: scale(15),
      borderRadius: scale(SCREEN_BORDER_RADIUS),
      height: scale(TILE_HEIGHT),
      minHeight: scale(TILE_HEIGHT),
      justifyContent: 'space-between' as const,
      position: 'relative' as const,
    },
  ];

  if (!isReadyToShow) {
    return <View style={tileStyles} />;
  }

  const tileContent = (
    <>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(5) }}>
        <Svg {...iconConfig} />
        <Text style={[font[titleConfig.fontType], { color: C(titleConfig.color) }]}>{i18n.t(titleConfig.text)}</Text>
      </View>

      {timeConfig.timeValue != null &&
        (isEditing && editable ? (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(8), flex: 1 }}>
            <View style={{ flex: 1 }}>
              <TimeTileInput C={C} value={inputValue} onChangeText={handleInputChange} />
            </View>
            <TimeTileEditActions
              C={C}
              onCancel={handleCancel}
              onConfirm={handleConfirm}
              updatedValue={parsedInputValue}
              originalValue={originalValue}
            />
          </View>
        ) : (
          <TimeDisplayInStaticFormat {...timeConfig} C={C} />
        ))}

      <Text style={[font[descriptionConfig.fontType], { color: C(descriptionConfig.color) }]}>
        {descriptionConfig?.textAlreadyTranslated ? descriptionConfig.text : i18n.t(descriptionConfig.text)}
      </Text>

      <View
        ref={dotRef}
        style={{
          width: scale(DOT_SIZE),
          height: scale(DOT_SIZE),
          position: 'absolute',
          bottom: -scale(DOT_SIZE) / 2,
          backgroundColor: C(titleConfig.color),
          borderRadius: scale(scale(DOT_SIZE) / 2),
          alignSelf: 'center',
        }}
      />
    </>
  );

  if (editable && !isEditing) {
    return (
      <TouchableElement onPress={handleTilePress} style={{ flex: 1 }}>
        {({ boxStyle, pressHoverColor }) => (
          <View ref={tileRef} style={[boxStyle, tileStyles, { backgroundColor: C(pressHoverColor('Background/Light/Field')) }]}>
            {tileContent}
          </View>
        )}
      </TouchableElement>
    );
  }

  if (isEditing) {
    return (
      <OutsidePressHandler onOutsidePress={handleCancel}>
        <View ref={tileRef} style={tileStyles}>
          {tileContent}
        </View>
      </OutsidePressHandler>
    );
  }

  return (
    <View ref={tileRef} style={tileStyles}>
      {tileContent}
    </View>
  );
};

export default ColorWrapper(TimeTile);
